import React, { useState, useEffect } from 'react';
import { Users, Plus, Trash2, Phone, ToggleLeft, ToggleRight, Settings, Clock } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const ROUTING_MODES = [
  {
    key: 'sequential',
    label: 'Sequential',
    description: 'Rings agents one by one in priority order. If agent 1 misses — tries agent 2, then 3.',
    icon: '1️⃣'
  },
  {
    key: 'round_robin',
    label: 'Round Robin',
    description: 'Rotates calls evenly across all agents. Each agent gets equal share.',
    icon: '🔄'
  },
  {
    key: 'simultaneous',
    label: 'Simultaneous',
    description: 'Rings all agents at once. First to answer gets the call.',
    icon: '📢'
  },
];

function authHeaders() {
  const token = localStorage.getItem('cia_token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
}

export default function Team() {
  const [agents, setAgents] = useState([]);
  const [plan, setPlan] = useState('starter');
  const [limit, setLimit] = useState(1);
  const [slotsRemaining, setSlotsRemaining] = useState(0);
  const [routingConfig, setRoutingConfig] = useState(null);
  const [businessHours, setBusinessHours] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [activeTab, setActiveTab] = useState('agents');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add agent form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAgent, setNewAgent] = useState({ name: '', phone: '', email: '' });
  const [addError, setAddError] = useState('');

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([loadAgents(), loadRoutingConfig(), loadBusinessHours(), loadPerformance()]);
    setLoading(false);
  };

  const loadAgents = async () => {
    try {
      const res = await fetch(`${API_BASE}/agents/`, { headers: authHeaders() });
      const data = await res.json();
      setAgents(data.agents || []);
      setPlan(data.plan || 'starter');
      setLimit(data.limit || 1);
      setSlotsRemaining(data.slots_remaining || 0);
    } catch (e) {
      console.error('Load agents error:', e);
    }
  };

  const loadRoutingConfig = async () => {
    try {
      const res = await fetch(`${API_BASE}/agents/routing/config`, { headers: authHeaders() });
      const data = await res.json();
      setRoutingConfig(data);
    } catch (e) {
      console.error('Load routing config error:', e);
    }
  };

  const loadBusinessHours = async () => {
    try {
      const res = await fetch(`${API_BASE}/agents/hours`, { headers: authHeaders() });
      const data = await res.json();
      setBusinessHours(data.hours || []);
    } catch (e) {
      console.error('Load hours error:', e);
    }
  };

  const loadPerformance = async () => {
    try {
      const res = await fetch(`${API_BASE}/agents/performance`, { headers: authHeaders() });
      const data = await res.json();
      setPerformance(data.performance || []);
    } catch (e) {
      console.error('Load performance error:', e);
    }
  };

  const handleAddAgent = async () => {
    setAddError('');
    if (!newAgent.name.trim() || !newAgent.phone.trim()) {
      setAddError('Name and phone are required');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/agents/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(newAgent),
      });
      const data = await res.json();
      if (data.error) {
        setAddError(data.error);
      } else {
        setNewAgent({ name: '', phone: '', email: '' });
        setShowAddForm(false);
        await loadAgents();
        setSuccess('Agent added successfully');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (e) {
      setAddError('Failed to add agent');
    }
    setSaving(false);
  };

  const handleRemoveAgent = async (agentId, agentName) => {
    if (!window.confirm(`Remove ${agentName} from your team?`)) return;
    try {
      await fetch(`${API_BASE}/agents/${agentId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      await loadAgents();
      setSuccess('Agent removed');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError('Failed to remove agent');
    }
  };

  const handleToggleAgent = async (agent) => {
    try {
      await fetch(`${API_BASE}/agents/${agent.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ is_active: !agent.is_active }),
      });
      await loadAgents();
    } catch (e) {
      setError('Failed to update agent');
    }
  };

  const handleSaveRouting = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/agents/routing/config`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(routingConfig),
      });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
      } else {
        setSuccess('Routing settings saved');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (e) {
      setError('Failed to save routing settings');
    }
    setSaving(false);
  };

  const handleSaveHours = async () => {
    setSaving(true);
    try {
      await fetch(`${API_BASE}/agents/hours`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ hours: businessHours }),
      });
      setSuccess('Business hours saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError('Failed to save business hours');
    }
    setSaving(false);
  };

  const updateHours = (index, field, value) => {
    const updated = [...businessHours];
    updated[index] = { ...updated[index], [field]: value };
    setBusinessHours(updated);
  };

  if (loading) {
    return (
      <div style={{ padding: 32, color: 'var(--text-secondary)', textAlign: 'center' }}>
        Loading team settings...
      </div>
    );
  }

  return (
    <div className="page-wrap" style={{ maxWidth: 860, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
          Team & Routing
        </h1>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          Manage your sales agents, call routing, and business hours.
          <span style={{
            marginLeft: 10,
            background: 'var(--blue)',
            color: 'white',
            fontSize: 11,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 99,
            textTransform: 'uppercase',
          }}>
            {plan} — {limit} agent{limit > 1 ? 's' : ''}
          </span>
        </p>
      </div>

      {/* Success/Error */}
      {success && (
        <div style={{ background: 'rgba(0,200,150,0.1)', border: '1px solid var(--green)', borderRadius: 8, padding: '10px 16px', marginBottom: 16, color: 'var(--green)', fontSize: 13 }}>
          ✅ {success}
        </div>
      )}
      {error && (
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', borderRadius: 8, padding: '10px 16px', marginBottom: 16, color: '#ef4444', fontSize: 13 }}>
          ❌ {error}
        </div>
      )}

      {/* Tabs */}
      <div className="tab-scroll-bar">
        {[
          { key: 'agents', label: 'Agents', icon: Users },
          { key: 'routing', label: 'Routing', icon: Settings },
          { key: 'hours', label: 'Business Hours', icon: Clock },
          { key: 'performance', label: 'Performance', icon: Phone },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px',
              background: 'none', border: 'none',
              borderBottom: activeTab === tab.key ? '2px solid var(--blue)' : '2px solid transparent',
              color: activeTab === tab.key ? 'var(--text-primary)' : 'var(--text-secondary)',
              fontWeight: activeTab === tab.key ? 600 : 400,
              fontSize: 13, cursor: 'pointer',
              marginBottom: -1,
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >
            <tab.icon size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* AGENTS TAB */}
      {activeTab === 'agents' && (
        <div>
          {/* Agent slots info */}
          <div style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            padding: '14px 18px',
            marginBottom: 20,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 13,
          }}>
            <span style={{ color: 'var(--text-secondary)' }}>
              {agents.filter(a => a.is_active).length} of {limit} agent slots used
            </span>
            {slotsRemaining === 0 ? (
              <span style={{ color: '#f59e0b', fontWeight: 600 }}>
                Upgrade to add more agents
              </span>
            ) : (
              <span style={{ color: 'var(--green)', fontWeight: 600 }}>
                {slotsRemaining} slot{slotsRemaining > 1 ? 's' : ''} available
              </span>
            )}
          </div>

          {/* Agent list */}
          {agents.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: 'var(--text-muted)',
              background: 'var(--bg-secondary)',
              borderRadius: 10,
              border: '1px dashed var(--border)',
              marginBottom: 16,
            }}>
              <Users size={32} style={{ marginBottom: 12, opacity: 0.4 }} />
              <p style={{ fontSize: 14, marginBottom: 4 }}>No agents added yet</p>
              <p style={{ fontSize: 12 }}>Add your first sales agent below. All calls will currently go to you.</p>
            </div>
          ) : (
            <div style={{ marginBottom: 16 }}>
              {agents.map((agent, index) => {
                const perf = performance.find(p => p.agent_id === agent.id);
                return (
                  <div key={agent.id} style={{
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    borderRadius: 10,
                    padding: '14px 18px',
                    marginBottom: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    opacity: agent.is_active ? 1 : 0.5,
                  }}>
                    {/* Priority badge */}
                    <div style={{
                      width: 28, height: 28,
                      background: agent.is_active ? 'var(--blue)' : 'var(--border)',
                      borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 700, color: 'white', flexShrink: 0,
                    }}>
                      {agent.priority}
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {agent.name}
                        {!agent.is_active && (
                          <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>
                            (inactive)
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        📞 {agent.phone}
                        {agent.email && ` · ✉️ ${agent.email}`}
                      </div>
                      {perf && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                          <span>{perf.total_calls} calls</span>
                          <span>{perf.answer_rate}% answered</span>
                          <span>{perf.sales_leads} leads</span>
                          {perf.won_deals > 0 && <span style={{ color: 'var(--green)' }}>{perf.won_deals} won</span>}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                      <button
                        onClick={() => handleToggleAgent(agent)}
                        title={agent.is_active ? 'Deactivate' : 'Activate'}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: agent.is_active ? 'var(--green)' : 'var(--text-muted)', padding: 4 }}
                      >
                        {agent.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                      </button>
                      <button
                        onClick={() => handleRemoveAgent(agent.id, agent.name)}
                        title="Remove agent"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 4 }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add agent form */}
          {showAddForm ? (
            <div style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: 20,
            }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'var(--text-primary)' }}>
                Add New Agent
              </h3>
              {addError && (
                <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 12 }}>{addError}</div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <input
                  placeholder="Full name *"
                  value={newAgent.name}
                  onChange={e => setNewAgent(p => ({ ...p, name: e.target.value }))}
                  style={{
                    padding: '10px 14px', borderRadius: 8,
                    border: '1px solid var(--border)',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)', fontSize: 13,
                  }}
                />
                <input
                  placeholder="Phone number with country code (e.g. +19175551234) *"
                  value={newAgent.phone}
                  onChange={e => setNewAgent(p => ({ ...p, phone: e.target.value }))}
                  style={{
                    padding: '10px 14px', borderRadius: 8,
                    border: '1px solid var(--border)',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)', fontSize: 13,
                  }}
                />
                <input
                  placeholder="Email (optional)"
                  value={newAgent.email}
                  onChange={e => setNewAgent(p => ({ ...p, email: e.target.value }))}
                  style={{
                    padding: '10px 14px', borderRadius: 8,
                    border: '1px solid var(--border)',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)', fontSize: 13,
                  }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={handleAddAgent}
                    disabled={saving}
                    style={{
                      flex: 1, padding: '10px 16px',
                      background: 'var(--blue)', color: 'white',
                      border: 'none', borderRadius: 8,
                      fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    {saving ? 'Adding...' : 'Add Agent'}
                  </button>
                  <button
                    onClick={() => { setShowAddForm(false); setAddError(''); }}
                    style={{
                      padding: '10px 16px',
                      background: 'none', color: 'var(--text-secondary)',
                      border: '1px solid var(--border)', borderRadius: 8,
                      fontSize: 13, cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : (
            slotsRemaining > 0 && (
              <button
                onClick={() => setShowAddForm(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 18px',
                  background: 'var(--blue)', color: 'white',
                  border: 'none', borderRadius: 8,
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >
                <Plus size={16} /> Add Agent
              </button>
            )
          )}
        </div>
      )}

      {/* ROUTING TAB */}
      {activeTab === 'routing' && routingConfig && (
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>
            Call Routing Mode
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
            {ROUTING_MODES.map(mode => (
              <div
                key={mode.key}
                onClick={() => setRoutingConfig(p => ({ ...p, routing_mode: mode.key }))}
                style={{
                  background: routingConfig.routing_mode === mode.key ? 'rgba(59,130,246,0.08)' : 'var(--bg-secondary)',
                  border: `1px solid ${routingConfig.routing_mode === mode.key ? 'var(--blue)' : 'var(--border)'}`,
                  borderRadius: 10, padding: '14px 18px',
                  cursor: 'pointer', display: 'flex', gap: 14, alignItems: 'flex-start',
                }}
              >
                <span style={{ fontSize: 20 }}>{mode.icon}</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
                    {mode.label}
                    {routingConfig.routing_mode === mode.key && (
                      <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--blue)', fontWeight: 400 }}>● Active</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                    {mode.description}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'block', marginBottom: 8 }}>
              Ring timeout per agent (seconds)
            </label>
            <input
              type="number"
              min="10" max="60"
              value={routingConfig.ring_timeout || 20}
              onChange={e => setRoutingConfig(p => ({ ...p, ring_timeout: parseInt(e.target.value) }))}
              style={{
                padding: '10px 14px', borderRadius: 8, width: 120,
                border: '1px solid var(--border)',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)', fontSize: 13,
              }}
            />
            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
              How long to ring each agent before trying the next (10-60 seconds)
            </p>
          </div>

          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <button
                onClick={() => setRoutingConfig(p => ({ ...p, missed_call_sms_enabled: !p.missed_call_sms_enabled }))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: routingConfig.missed_call_sms_enabled ? 'var(--green)' : 'var(--text-muted)', padding: 0 }}
              >
                {routingConfig.missed_call_sms_enabled ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
              </button>
              <label style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>
                Send SMS when call is missed
              </label>
            </div>
            {routingConfig.missed_call_sms_enabled && (
              <textarea
                value={routingConfig.missed_call_sms_message || ''}
                onChange={e => setRoutingConfig(p => ({ ...p, missed_call_sms_message: e.target.value }))}
                rows={3}
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: 8,
                  border: '1px solid var(--border)',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)', fontSize: 13,
                  resize: 'vertical',
                }}
              />
            )}
          </div>

          <button
            onClick={handleSaveRouting}
            disabled={saving}
            style={{
              padding: '10px 24px',
              background: 'var(--blue)', color: 'white',
              border: 'none', borderRadius: 8,
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {saving ? 'Saving...' : 'Save Routing Settings'}
          </button>
        </div>
      )}

      {/* BUSINESS HOURS TAB */}
      {activeTab === 'hours' && (
        <div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
            Calls outside business hours will play a closed message and send an SMS to the caller.
          </p>

          <div style={{ marginBottom: 24 }}>
            {businessHours.map((day, index) => (
              <div key={day.day_of_week} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                flexWrap: 'wrap',
                padding: '12px 0',
                borderBottom: '1px solid var(--border)',
              }}>
                {/* Day toggle */}
                <button
                  onClick={() => updateHours(index, 'is_open', !day.is_open)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: day.is_open ? 'var(--green)' : 'var(--text-muted)', padding: 0, flexShrink: 0 }}
                >
                  {day.is_open ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
                </button>

                {/* Day name */}
                <span style={{ width: 90, fontSize: 13, color: day.is_open ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: day.is_open ? 500 : 400, flexShrink: 0 }}>
                  {day.day_name || DAYS[day.day_of_week]}
                </span>

                {day.is_open ? (
                  <>
                    <input
                      type="time"
                      value={(day.open_time || '09:00:00').substring(0, 5)}
                      onChange={e => updateHours(index, 'open_time', e.target.value + ':00')}
                      style={{
                        padding: '6px 10px', borderRadius: 6,
                        border: '1px solid var(--border)',
                        background: 'var(--bg-secondary)',
                        color: 'var(--text-primary)', fontSize: 13,
                      }}
                    />
                    <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>to</span>
                    <input
                      type="time"
                      value={(day.close_time || '18:00:00').substring(0, 5)}
                      onChange={e => updateHours(index, 'close_time', e.target.value + ':00')}
                      style={{
                        padding: '6px 10px', borderRadius: 6,
                        border: '1px solid var(--border)',
                        background: 'var(--bg-secondary)',
                        color: 'var(--text-primary)', fontSize: 13,
                      }}
                    />
                  </>
                ) : (
                  <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Closed</span>
                )}
              </div>
            ))}
          </div>

          <button
            onClick={handleSaveHours}
            disabled={saving}
            style={{
              padding: '10px 24px',
              background: 'var(--blue)', color: 'white',
              border: 'none', borderRadius: 8,
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {saving ? 'Saving...' : 'Save Business Hours'}
          </button>
        </div>
      )}

      {/* PERFORMANCE TAB */}
      {activeTab === 'performance' && (
        <div>
          {performance.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
              <Phone size={32} style={{ marginBottom: 12, opacity: 0.4 }} />
              <p>No performance data yet. Add agents and start receiving calls.</p>
            </div>
          ) : (
            <div>
              {performance.map(agent => (
                <div key={agent.agent_id} style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: 10, padding: '16px 20px',
                  marginBottom: 12,
                }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{agent.agent_name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{agent.agent_phone}</div>
                    </div>
                    <div style={{
                      fontSize: 11, fontWeight: 600,
                      color: agent.answer_rate >= 80 ? 'var(--green)' : agent.answer_rate >= 50 ? '#f59e0b' : '#ef4444',
                      background: agent.answer_rate >= 80 ? 'rgba(0,200,150,0.1)' : agent.answer_rate >= 50 ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)',
                      padding: '4px 10px', borderRadius: 99,
                      flexShrink: 0,
                    }}>
                      {agent.answer_rate}% answer rate
                    </div>
                  </div>

                  <div className="perf-stat-grid" style={{ gap: 12 }}>
                    {[
                      { label: 'Total Calls', value: agent.total_calls },
                      { label: 'Answered', value: agent.answered_calls },
                      { label: 'Sales Leads', value: agent.sales_leads },
                      { label: 'Deals Won', value: agent.won_deals, highlight: true },
                    ].map(stat => (
                      <div key={stat.label} style={{
                        background: 'var(--bg-primary)', borderRadius: 8, padding: '10px 14px', textAlign: 'center',
                      }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: stat.highlight && stat.value > 0 ? 'var(--green)' : 'var(--text-primary)' }}>
                          {stat.value}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{stat.label}</div>
                      </div>
                    ))}
                  </div>

                  {agent.avg_call_duration_seconds > 0 && (
                    <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)' }}>
                      Avg call duration: {Math.floor(agent.avg_call_duration_seconds / 60)}m {agent.avg_call_duration_seconds % 60}s
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
